import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSiteSettings } from "../core/site";
// The homepage is the common entry point, so it stays in the main chunk.
import { HomePage } from "../features/home";

// Every other route is split out by FILE path (not the feature index) so each
// page stays its own chunk. Article pages in particular pull in the markdown
// and diagram machinery, which no homepage visitor should download; admin
// should never weigh on public routes.
const NotFoundPage = lazy(() => import("../features/not-found/NotFoundPage"));
const AboutPage = lazy(() => import("../features/about/AboutPage"));
const ContactPage = lazy(() => import("../features/contact/ContactPage"));
const JournalPage = lazy(() => import("../features/journal/JournalPage"));
const JournalDetail = lazy(() => import("../features/journal/JournalDetail"));
const ImpactPage = lazy(() => import("../features/impact/ImpactPage"));
const ImpactArticle = lazy(() => import("../features/impact/ImpactArticle"));
const VenturesPage = lazy(() => import("../features/ventures/VenturesPage"));
const ServicesPage = lazy(() => import("../features/services/ServicesPage"));
const AdminPage = lazy(() => import("../features/admin/AdminPage"));

/** Old URLs and where they now live, in one place. */
export const REDIRECTS = {
	"/startups": "/ventures",
	"/2am": "/ventures",
	"/azertylabs": "/ventures",
	"/rnd": "/impact",
	// Case studies are pinned Impact pages now; the old per-institution pages
	// (including /impact/ircam-amplify) land on the index.
	"/impact/:institution": "/impact",
};

// In showcase mode (Settings → open to work off) the Services page is gone:
// old links land on About instead of a page that sells.
function ServicesRoute() {
	const { openToWork } = useSiteSettings();
	return openToWork ? <ServicesPage /> : <Navigate replace to="/about" />;
}

// A page switched off in Settings → pages: its URLs land on the homepage,
// unless the visitor came through the private share link. The share key is
// checked once the settings load, so wait for that before sending anyone away.
function PageRoute({ page, children }) {
	const { pages, loaded, shared } = useSiteSettings();
	if (pages[page] || shared) return children;
	return loaded ? <Navigate replace to="/" /> : null;
}

/**
 * The route table: every public page, the legacy redirects, admin, and the
 * not-found fallback. Must render inside a Router and a Suspense boundary.
 */
export function AppRoutes() {
	return (
		<Routes>
			<Route path="/" element={<HomePage />} />
			<Route
				path="/about"
				element={
					<PageRoute page="about">
						<AboutPage />
					</PageRoute>
				}
			/>
			<Route path="/services" element={<ServicesRoute />} />
			<Route path="/contact" element={<ContactPage />} />
			<Route
				path="/ventures"
				element={
					<PageRoute page="ventures">
						<VenturesPage />
					</PageRoute>
				}
			/>
			<Route
				path="/impact"
				element={
					<PageRoute page="impact">
						<ImpactPage />
					</PageRoute>
				}
			/>
			<Route
				path="/impact/:institution/:article"
				element={
					<PageRoute page="impact">
						<ImpactArticle />
					</PageRoute>
				}
			/>
			<Route
				path="/journal"
				element={
					<PageRoute page="journal">
						<JournalPage />
					</PageRoute>
				}
			/>
			<Route
				path="/journal/:slug"
				element={
					<PageRoute page="journal">
						<JournalDetail />
					</PageRoute>
				}
			/>
			{Object.entries(REDIRECTS).map(([from, to]) => (
				<Route key={from} path={from} element={<Navigate replace to={to} />} />
			))}
			<Route path="/admin/*" element={<AdminPage />} />
			<Route path="*" element={<NotFoundPage />} />
		</Routes>
	);
}
